import { useEffect, useMemo, useState } from "react";
import api from "../../auth/api";

/**
 * Formulario de creación/edición de Bots
 * Props:
 * - value?: objeto bot existente para editar (o null para crear)
 * - busy?: boolean (deshabilita botones mientras guarda)
 * - onSubmit?: (payload) => Promise<void> | void
 * - onCancel?: () => void
 */
export default function BotForm({
  value = null,
  busy = false,
  onSubmit,
  onCancel,
}) {
  const [name, setName] = useState("");
  const [kind, setKind] = useState("generic");
  const [baseUrl, setBaseUrl] = useState("");
  const [webhookPath, setWebhookPath] = useState("");
  const [authType, setAuthType] = useState("none");
  const [basicUser, setBasicUser] = useState("");
  const [basicPass, setBasicPass] = useState("");
  const [bearer, setBearer] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [flowId, setFlowId] = useState("");
  const [flows, setFlows] = useState([]);

  // Cargar flujos disponibles
  useEffect(() => {
    let mounted = true;
    async function loadFlows() {
      try {
        const { data } = await api.get("/flows");
        if (mounted) {
          setFlows(data?.rows || []);
        }
      } catch (error) {
        console.error("Error al cargar flujos:", error);
      }
    }
    loadFlows();
    return () => {
      mounted = false;
    };
  }, []);

  // Cargar valores cuando se edita
  useEffect(() => {
    if (!value) return;
    setName(value.name || "");
    setKind(value.kind || "generic");
    setBaseUrl(value.base_url || "");
    setWebhookPath(value.webhook_path || "");
    setAuthType(value.auth_type || "none");
    setBasicUser(value.basic_user || "");
    setBasicPass(value.basic_pass || "");
    setBearer(value.bearer_token || "");
    setIsActive(!!value.is_active);
    setFlowId(value.flow_id || "");
  }, [value]);

  const canSubmit = useMemo(() => {
    if (!name.trim()) return false;
    if (kind === "flow") return !!flowId;
    if (authType === "basic") return !!basicUser.trim() && !!basicPass;
    if (authType === "bearer") return !!bearer.trim();
    return true;
  }, [name, kind, flowId, authType, basicUser, basicPass, bearer]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    const payload = {
      name: name.trim(),
      kind,
      base_url: kind === "flow" ? "" : baseUrl.trim(),
      webhook_path: kind === "flow" ? "" : webhookPath.trim(),
      auth_type: kind === "flow" ? "none" : authType,
      basic_user: kind !== "flow" && authType === "basic" ? basicUser : "",
      basic_pass: kind !== "flow" && authType === "basic" ? basicPass : "",
      bearer_token: kind !== "flow" && authType === "bearer" ? bearer : "",
      is_active: isActive ? 1 : 0,
      flow_id: kind === "flow" ? flowId : null,
    };
    await onSubmit?.(payload);
  };

  return (
    <form onSubmit={submit} className="card" style={{ padding: 12, display: "grid", gap: 10 }}>
      <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
        <label>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Nombre *</div>
          <input
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Bot Atención GYE"
          />
        </label>

        <label>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Tipo (kind)</div>
          <select
            className="input"
            value={kind}
            onChange={(e) => setKind(e.target.value)}
          >
            <option value="generic">Genérico (webhook)</option>
            <option value="botpress">Botpress</option>
            <option value="n8n">n8n</option>
            <option value="dialogflow">Dialogflow</option>
            <option value="flow">Flow 3.0 (Visual)</option>
          </select>
        </label>

        {kind === "flow" && (
          <label>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Flujo Diagramado *</div>
            <select
              className="input"
              value={flowId}
              onChange={(e) => setFlowId(e.target.value)}
            >
              <option value="">Seleccionar flujo...</option>
              {flows.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name} ({f.id})
                </option>
              ))}
            </select>
          </label>
        )}

        {kind !== "flow" && (
          <>
            <label>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>Base URL</div>
              <input
                className="input"
                value={baseUrl}
                onChange={(e) => setBaseUrl(e.target.value)}
                placeholder="https://mi-bot/api"
              />
            </label>

            <label>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>Webhook Path</div>
              <input
                className="input"
                value={webhookPath}
                onChange={(e) => setWebhookPath(e.target.value)}
                placeholder="/webhook/inbound"
              />
            </label>

            <label>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>Auth</div>
              <select
                className="input"
                value={authType}
                onChange={(e) => setAuthType(e.target.value)}
              >
                <option value="none">Sin auth</option>
                <option value="basic">Basic</option>
                <option value="bearer">Bearer</option>
              </select>
            </label>

            {authType === "basic" && (
              <>
                <label>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>Basic User</div>
                  <input
                    className="input"
                    value={basicUser}
                    onChange={(e) => setBasicUser(e.target.value)}
                    placeholder="usuario"
                  />
                </label>
                <label>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>Basic Pass</div>
                  <input
                    className="input"
                    type="password"
                    value={basicPass}
                    onChange={(e) => setBasicPass(e.target.value)}
                    placeholder="••••••••"
                  />
                </label>
              </>
            )}

            {authType === "bearer" && (
              <label>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>Bearer Token</div>
                <input
                  className="input"
                  value={bearer}
                  onChange={(e) => setBearer(e.target.value)}
                  placeholder="eyJhbGciOi..."
                />
              </label>
            )}
          </>
        )}

        <label style={{ display: "flex", alignItems: "end", gap: 8 }}>
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
          />
          Activo
        </label>
      </div>

      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
        <button className="btn" type="submit" disabled={!canSubmit || busy}>
          {busy ? "Guardando..." : value ? "Guardar cambios" : "Crear bot"}
        </button>
        <button className="btn btn-muted" type="button" onClick={onCancel} disabled={busy}>
          Cancelar
        </button>
      </div>
    </form>
  );
}
